import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  GameStatePayload,
  RoomStatePayload,
  RoundRevealPayload,
  ServerEventPayload,
} from "@hitster/shared";
import { useSocket } from "./useSocket";
import { audioManager } from "../lib/audioManager";
import { markStreamPlayGesture, warmupEmbeddedStream } from "../providers/EmbeddedStreamProvider";
import {
  getNickname,
  getReconnectToken,
  getRoomCode,
  setNickname as saveNickname,
  setPlayerId,
  setReconnectToken,
  setRoomCode,
  clearSession,
} from "../lib/storage";

interface GameContextValue {
  connected: boolean;
  connectionError: string | null;
  nickname: string;
  setNickname: (n: string) => void;
  room: RoomStatePayload | null;
  game: GameStatePayload | null;
  playerId: string | null;
  reconnectToken: string | null;
  lastReveal: RoundRevealPayload | null;
  emit: (event: string, payload?: unknown) => boolean;
  emitUserAction: (event: string, payload?: unknown) => boolean;
  leaveLobby: () => void;
  actionError: string | null;
  clearActionError: () => void;
  hintDecade: number | null;
  hintsRemaining: number | null;
  audioLoadFailed: boolean;
  audioLoading: boolean;
}

const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const { socket, connected, connectionError } = useSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const userActionRef = useRef(false);
  const resyncInProgressRef = useRef(false);
  const inSessionRef = useRef(false);
  const [nickname, setNicknameState] = useState(getNickname);
  const [room, setRoom] = useState<RoomStatePayload | null>(null);
  const [game, setGame] = useState<GameStatePayload | null>(null);
  const [playerId, setPlayerIdState] = useState<string | null>(null);
  const [reconnectToken, setReconnectTokenState] = useState<string | null>(getReconnectToken() || null);
  const [lastReveal, setLastReveal] = useState<RoundRevealPayload | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [hintDecade, setHintDecade] = useState<number | null>(null);
  const [hintsRemaining, setHintsRemaining] = useState<number | null>(null);
  const [audioLoadFailed, setAudioLoadFailed] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const pendingAudioRef = useRef<ServerEventPayload<"audio:play"> | null>(null);
  const prevRoundIdRef = useRef<string | null>(null);
  const lastAudioPlayKeyRef = useRef<string | null>(null);
  const audioPlayGenRef = useRef(0);
  const playerIdRef = useRef<string | null>(null);
  const activePlayerIdRef = useRef<string | null>(null);

  playerIdRef.current = playerId;
  activePlayerIdRef.current = game?.activePlayerId ?? null;

  const retryPendingAudio = useCallback(async () => {
    const pending = pendingAudioRef.current;
    if (!pending || !audioManager.isUnlocked()) return;
    if (lastAudioPlayKeyRef.current === pending.roundId) return;
    lastAudioPlayKeyRef.current = pending.roundId;
    const gen = ++audioPlayGenRef.current;
    const ok = await audioManager.play(pending.source, pending.scheduledAt);
    if (gen !== audioPlayGenRef.current) return;
    if (ok) pendingAudioRef.current = null;
    else lastAudioPlayKeyRef.current = null;
  }, []);

  useEffect(() => {
    inSessionRef.current = Boolean(room && playerId);
  }, [room, playerId]);

  const setNickname = useCallback((n: string) => {
    setNicknameState(n);
    saveNickname(n);
  }, []);

  const emit = useCallback(
    (event: string, payload?: unknown): boolean => {
      if (!socket?.connected) return false;
      socket.emit(event, payload);
      return true;
    },
    [socket],
  );

  const emitUserAction = useCallback(
    (event: string, payload?: unknown): boolean => {
      if (event === "audio:request-play") {
        markStreamPlayGesture();
        lastAudioPlayKeyRef.current = null;
      }
      void audioManager.unlockWithUserGesture().then(() => retryPendingAudio());
      userActionRef.current = true;
      const sent = emit(event, payload);
      if (!sent) userActionRef.current = false;
      return sent;
    },
    [emit, retryPendingAudio],
  );

  useEffect(() => {
    if (location.pathname === "/game") warmupEmbeddedStream();
  }, [location.pathname]);

  useEffect(() => {
    const onGesture = () => {
      void audioManager.unlockWithUserGesture().then(() => retryPendingAudio());
    };
    document.addEventListener("pointerdown", onGesture, { capture: true, passive: true });
    document.addEventListener("keydown", onGesture, { capture: true, passive: true });
    return () => {
      document.removeEventListener("pointerdown", onGesture, { capture: true });
      document.removeEventListener("keydown", onGesture, { capture: true });
    };
  }, [retryPendingAudio]);

  useEffect(() => audioManager.subscribe(() => {
    void retryPendingAudio();
  }), [retryPendingAudio]);

  const handleSilentSessionLoss = useCallback(() => {
    clearSession();
    setRoom(null);
    setGame(null);
    setPlayerIdState(null);
    setReconnectTokenState(null);
    if (location.pathname === "/lobby" || location.pathname === "/game") {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!socket) return;

    const onRoomState = (payload: RoomStatePayload) => {
      userActionRef.current = false;
      resyncInProgressRef.current = false;
      setRoom({
        ...payload,
        players: [...payload.players],
        settings: { ...payload.settings },
      });
      setRoomCode(payload.code);
      setGame(payload.game ? { ...payload.game, players: [...payload.game.players] } : null);
    };
    const onGameState = (payload: GameStatePayload) => {
      if (payload.roundId !== prevRoundIdRef.current) {
        setHintDecade(null);
        setHintsRemaining(null);
        lastAudioPlayKeyRef.current = null;
        audioPlayGenRef.current += 1;
        pendingAudioRef.current = null;
        setAudioLoadFailed(false);
        setAudioLoading(false);
        audioManager.stop();
      }
      prevRoundIdRef.current = payload.roundId;
      setGame({
        ...payload,
        players: payload.players.map((p) => ({
          ...p,
          timeline: [...p.timeline],
        })),
        settings: { ...payload.settings },
        eventLog: [...payload.eventLog],
      });
    };
    const onReveal = (payload: RoundRevealPayload) => setLastReveal(payload);
    const onError = (payload: { message: string }) => {
      const fatal =
        payload.message.includes("introuvable") ||
        payload.message.includes("expirée") ||
        payload.message.includes("expiré");

      const wasUserAction = userActionRef.current;
      userActionRef.current = false;

      if (wasUserAction) {
        setActionError(payload.message);
        return;
      }

      if (resyncInProgressRef.current) {
        resyncInProgressRef.current = false;
        if (fatal) handleSilentSessionLoss();
        return;
      }
      if (inSessionRef.current && !fatal) return;
      const onFormPage = location.pathname === "/create" || location.pathname === "/join";
      if (onFormPage && !fatal) return;
      if (fatal || !inSessionRef.current) handleSilentSessionLoss();
    };
    const onSessionInfo = (payload: { playerId: string; reconnectToken: string; roomCode: string }) => {
      setPlayerIdState(payload.playerId);
      setPlayerId(payload.playerId);
      setReconnectTokenState(payload.reconnectToken);
      setReconnectToken(payload.reconnectToken);
      setRoomCode(payload.roomCode);
    };
    const onKicked = () => {
      clearSession();
      setRoom(null);
      setGame(null);
      setPlayerIdState(null);
      setReconnectTokenState(null);
      setLastReveal(null);
      navigate("/", { replace: true });
    };
    const onHintRevealed = (payload: ServerEventPayload<"round:hint-revealed">) => {
      setHintDecade(payload.decade);
      setHintsRemaining(payload.hintsRemaining);
    };
    const onAudioPlay = async (payload: ServerEventPayload<"audio:play">) => {
      const playKey = payload.roundId;
      if (lastAudioPlayKeyRef.current === playKey) return;
      lastAudioPlayKeyRef.current = playKey;
      const gen = ++audioPlayGenRef.current;

      pendingAudioRef.current = payload;
      setAudioLoading(true);
      setAudioLoadFailed(false);
      const ok = await audioManager.play(payload.source, payload.scheduledAt);
      if (gen !== audioPlayGenRef.current) return;
      setAudioLoading(false);

      const isActivePlayer =
        playerIdRef.current !== null && playerIdRef.current === activePlayerIdRef.current;

      if (ok) {
        pendingAudioRef.current = null;
        setAudioLoadFailed(false);
        if (isActivePlayer) {
          emit("audio:playback-started", { roundId: payload.roundId });
        }
      } else {
        lastAudioPlayKeyRef.current = null;
        setAudioLoadFailed(true);
        if (isActivePlayer) {
          emit("audio:playback-failed", { roundId: payload.roundId });
        }
      }
    };

    socket.on("room:state", onRoomState);
    socket.on("game:state", onGameState);
    socket.on("round:reveal", onReveal);
    socket.on("round:hint-revealed", onHintRevealed);
    socket.on("room:error", onError);
    socket.on("session:info", onSessionInfo);
    socket.on("room:kicked", onKicked);
    socket.on("audio:play", onAudioPlay);

    return () => {
      socket.off("room:state", onRoomState);
      socket.off("game:state", onGameState);
      socket.off("round:reveal", onReveal);
      socket.off("round:hint-revealed", onHintRevealed);
      socket.off("room:error", onError);
      socket.off("session:info", onSessionInfo);
      socket.off("room:kicked", onKicked);
      socket.off("audio:play", onAudioPlay);
    };
  }, [socket, handleSilentSessionLoss, navigate, location.pathname]);

  // Resynchronise la session à chaque connexion WebSocket (lobby / partie uniquement)
  useEffect(() => {
    if (!socket || !nickname) return;

    const resyncRoom = () => {
      if (location.pathname !== "/lobby" && location.pathname !== "/game") return;
      const code = getRoomCode();
      const token = getReconnectToken();
      if (!code || !token) return;
      userActionRef.current = false;
      resyncInProgressRef.current = true;
      socket.emit("room:join", { code, nickname, reconnectToken: token });
    };

    socket.on("connect", resyncRoom);
    if (socket.connected) resyncRoom();

    return () => {
      socket.off("connect", resyncRoom);
    };
  }, [socket, nickname, location.pathname]);

  // Resync périodique pour rattraper les événements manqués (latence / lag hébergeur)
  useEffect(() => {
    if (!socket || !room) return;
    if (location.pathname !== "/lobby" && location.pathname !== "/game") return;

    const sync = () => {
      if (socket.connected) socket.emit("room:sync");
    };

    sync();
    const interval = setInterval(sync, 3_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [socket, room, location.pathname]);

  const leaveLobby = useCallback(() => {
    emit("room:leave");
    clearSession();
    setRoom(null);
    setGame(null);
    setPlayerIdState(null);
    setReconnectTokenState(null);
    setLastReveal(null);
    setHintDecade(null);
    setHintsRemaining(null);
    navigate("/", { replace: true });
  }, [emit, navigate]);

  const value: GameContextValue = {
    connected,
    connectionError,
    nickname,
    setNickname,
    room,
    game,
    playerId,
    reconnectToken,
    lastReveal,
    emit,
    emitUserAction,
    leaveLobby,
    actionError,
    clearActionError: () => setActionError(null),
    hintDecade,
    hintsRemaining,
    audioLoadFailed,
    audioLoading,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame hors provider");
  return ctx;
}
